<template>
  <div class="scanner">
    <video ref="videoRef" class="scanner-video" muted playsinline></video>
    <canvas ref="canvasRef" class="scanner-canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { scanImageData } from '@undecaf/zbar-wasm'

type ScannerResult = {
  decodedText: string
  formatName: string
}

type ZBarScanSymbol = {
  typeName: string
  decode: (encoding?: string) => string
  quality?: number
  orientation?: number
}

const props = withDefaults(defineProps<{
  stopOnSuccess?: boolean
}>(), {
  stopOnSuccess: true
})

const emit = defineEmits<{
  success: [value: string]
  detected: [result: ScannerResult]
  error: [message: string]
  ready: []
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const hasScanned = ref(false)
const isStarted = ref(false)
const isStarting = ref(false)
const streamRef = ref<MediaStream | null>(null)
const frameTimerRef = ref<number | null>(null)
const isDecodingFrame = ref(false)

const clearFrameTimer = () => {
  if (frameTimerRef.value !== null) {
    window.clearInterval(frameTimerRef.value)
    frameTimerRef.value = null
  }
}

const stopTracks = () => {
  if (!streamRef.value) {
    return
  }

  streamRef.value.getTracks().forEach(track => {
    track.stop()
  })
  streamRef.value = null
}

const getErrorMessage = (error: unknown) => {
  const name = typeof error === 'object' && error && 'name' in error ? String(error.name) : ''

  if (name === 'NotAllowedError') {
    return '未获得相机权限，请允许摄像头访问'
  }

  if (name === 'NotFoundError') {
    return '未检测到可用摄像头'
  }

  return '启动扫码失败，请检查摄像头权限或浏览器环境'
}

const stopScanner = async () => {
  if (!isStarted.value && !streamRef.value) {
    return
  }

  clearFrameTimer()
  stopTracks()

  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.srcObject = null
  }

  isStarted.value = false
  isDecodingFrame.value = false
}

const decodeCurrentFrame = async () => {
  if (isDecodingFrame.value || !isStarted.value || hasScanned.value) {
    return
  }

  const video = videoRef.value
  const canvas = canvasRef.value
  if (!video || !canvas) {
    return
  }

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !video.videoWidth || !video.videoHeight) {
    return
  }

  isDecodingFrame.value = true
  try {
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d', { willReadFrequently: true })

    if (!context) {
      return
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
    const symbols = await scanImageData(imageData)

    if (!symbols.length) {
      return
    }

    const symbol = symbols.find(item => item.decode()) as ZBarScanSymbol | undefined
    if (!symbol) {
      return
    }

    const decodedText = symbol.decode().trim()
    if (!decodedText) {
      return
    }

    if (hasScanned.value) {
      return
    }

    hasScanned.value = true
    const formatName = symbol.typeName || 'UNKNOWN'
    emit('success', decodedText)
    emit('detected', { decodedText, formatName })

    if (props.stopOnSuccess) {
      await stopScanner()
    }
  } finally {
    isDecodingFrame.value = false
  }
}

const startScanner = async () => {
  if (isStarting.value) {
    return
  }

  if (!videoRef.value) {
    emit('error', '扫码容器初始化失败，请刷新后重试')
    return
  }

  isStarting.value = true
  try {
    await stopScanner()

    hasScanned.value = false
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: false
    } satisfies MediaStreamConstraints

    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    streamRef.value = stream
    videoRef.value.srcObject = stream
    await videoRef.value.play()

    frameTimerRef.value = window.setInterval(() => {
      void decodeCurrentFrame()
    }, 150)

    isStarted.value = true
    emit('ready')
  } catch (error) {
    emit('error', getErrorMessage(error))
  } finally {
    isStarting.value = false
  }
}

const restartScanner = async () => {
  await startScanner()
}

defineExpose({
  restart: restartScanner,
  stop: stopScanner
})

onMounted(() => {
  startScanner()
})

onBeforeUnmount(() => {
  void stopScanner()
})
</script>

<style scoped>
.scanner {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 20px;
  background:
    radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.18) 0%, rgba(15, 23, 42, 0.22) 38%, rgba(2, 6, 23, 0.92) 100%),
    #020617;
  box-shadow:
    inset 0 0 0 1px rgba(148, 163, 184, 0.2),
    0 16px 40px rgba(2, 6, 23, 0.38);
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.05) contrast(1.04);
}

.scanner-canvas {
  display: none;
}
</style>
